import * as React from 'react'

import { PlayFilledIcon, VolumeOffIcon } from '@buffer-mono/popcorn'

import type { Video } from '~publish/legacy/composer/composer/entities/factories'

import styles from './VideoPlayer.module.css'

import type { JSX } from 'react'

const SoundOff = (): JSX.Element => {
  return (
    <div className={styles.soundOffWrapper}>
      <VolumeOffIcon size={10} />
    </div>
  )
}

export type PlayerProps = {
  video: Video
  width?: string
  height?: string
  showSoundOffIcon?: boolean
  hidePlayButton?: boolean
  autoPlay?: boolean
  loop?: boolean
  className?: string
  withBlur?: boolean
  playIcon?: React.ReactNode
}

const Player = ({
  video,
  width,
  height,
  showSoundOffIcon = false,
  className,
  hidePlayButton,
  autoPlay,
  loop,
  withBlur,
  playIcon,
}: PlayerProps): JSX.Element => {
  const { thumbnail, url } = video || {}

  const videoRef = React.useRef<HTMLVideoElement>(null)
  const playButtonRef = React.useRef<HTMLButtonElement>(null)

  const onClick = (): void => {
    if (!videoRef.current || !playButtonRef.current) {
      return
    }

    videoRef.current.muted = false
    void videoRef.current.play()
    videoRef.current.setAttribute('controls', 'true')
    playButtonRef.current.style.display = 'none'
  }

  const onEnded = (): void => {
    if (!videoRef.current || !playButtonRef.current) {
      return
    }

    videoRef.current.removeAttribute('controls')
    playButtonRef.current.style.display = 'flex'
  }

  return (
    <div
      data-testid="preview-video"
      className={className}
      style={{
        overflow: withBlur ? 'hidden' : 'visible',
      }}
    >
      {/* Keep preload="none" and no autoPlay. Otherwise every channel preview downloads the uploaded video from S3 again, which costs a few thousand USD a month in egress. */}
      <video
        aria-label="video-player"
        data-testid="video-player"
        ref={videoRef}
        poster={thumbnail}
        src={url}
        preload="none"
        muted
        onEnded={onEnded}
        crossOrigin={'anonymous'}
        height={height || '100%'}
        width={width || '100%'}
        style={{
          filter: withBlur ? 'blur(35px)' : 'none',
        }}
        autoPlay={autoPlay}
        loop={loop}
      />
      {!hidePlayButton && !withBlur && (
        <button type="button" onClick={onClick} ref={playButtonRef}>
          {playIcon || <PlayFilledIcon size={30} />}
        </button>
      )}
      {withBlur && <div className={styles.showMore}>Tap to reveal</div>}
      {showSoundOffIcon && <SoundOff />}
      <div className="overlay" />
    </div>
  )
}

export const VideoPlayer = (props: PlayerProps): JSX.Element => {
  if (props.className) {
    return <Player {...props} />
  }

  return <Player {...props} className={styles.wrapper} />
}
